import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    APIS,
    SF_BIDR_USR_SRCH,
    SG_BIDR_USR_EXCEL_LIST,
    SG_BIDR_USR_LIST,
    URLS,
} from "@/tra/tancis/ptli/usr/bidrusr/services/PtliBidrUsrService";
import dayjs from "dayjs";

/*
 * Bidder User Management List
 * !== 공매자 승인 요청 목록 ==!
 */
export const PTLI0911001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [load, setLoad] = useState(false);
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
            { area: "comnCd", comnCd: "COM0052" },
            { area: "comnCd", comnCd: "PTL0001" },
        ],
    });



    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Bidder User Management List Search !== 공매자 사용자 목록 검색 ==!
        bidrAprvUsrListSrch: useForm({
            defaultSchema: SF_BIDR_USR_SRCH,
            defaultValues: { ...pgeStore?.form },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Bidder User Management List !== 공매자 사용자 목록 ==!
        bidrAprvUsrList: useGrid({
            defaultSchema: SG_BIDR_USR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        bidrAprvUsrExcelList: useGrid({
            defaultSchema: SG_BIDR_USR_EXCEL_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Bidder User Management List !==  공매자 사용자 목록 조회==!
        getBidrUsrList: useFetch({
            api: (page = grid.bidrAprvUsrList.page) =>
                APIS.getBidrUsrList(form.bidrAprvUsrListSrch.getValues(), page, grid.bidrAprvUsrList.size),
            enabled: comnUtils.isEmpty(form.bidrAprvUsrListSrch.errors),
            key: [grid.bidrAprvUsrList.page, grid.bidrAprvUsrList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.bidrAprvUsrListSrch.getValues(),
                    page: grid.bidrAprvUsrList.page,
                    size: grid.bidrAprvUsrList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
        //Check Bidder User Management List !==  공매자 사용자 엑셀 목록 조회==!
        getBidrUsrExcelList: useFetch({
            api: (data: any) => (load ? APIS.getBidrUsrExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.bidrAprvUsrListSrch.errors),
            onSuccess: () => {
                if (load) {
                    grid.bidrAprvUsrExcelList.exportExcel({
                        // Internal Portal Customs User Information !== 내부 포털_세관_사용자 정보 ==!
                        excelName: `${t("L_INT_PTL")}_${t("L_ACTN_USR")}_${t("T_USR_INFO")}_${dayjs().format(
                            "DD/MM/YYYY HHmm",
                        )}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),
    };
    const render = {
        cell: {
            usrNm: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`/ptli/usr/bidrusr/ptli0911004q/${rowValues.xtrnUsrUuid}`}>{value}</Link>;
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: (page: number = 0) => {
            grid.bidrAprvUsrList.setPage(page);
            fetch.getBidrUsrList.fetch(page);
        },

        //Click Registration Button  !== 등록 버튼 클릭 ==!
        onNewBtnClick: () => navigate(`${URLS.ptli0911002s}`),
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getBidrUsrExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.bidrAprvUsrListSrch.schema.xtrnUsrIdntfNo} />
                            <Group.Control {...form.bidrAprvUsrListSrch.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.bidrAprvUsrListSrch.schema.usrTp} />
                            <Group.Control {...form.bidrAprvUsrListSrch.schema.actnCtgrCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.bidrAprvUsrListSrch.schema.ynCode} />
                            <Group.Control {...form.bidrAprvUsrListSrch.schema.usrStatCd}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.bidrAprvUsrListSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    setLoad(true);
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Grid
                            {...grid.bidrAprvUsrList.grid}
                            data={fetch.getBidrUsrList.data?.bidrUsrList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
            </Layout>
            <Group hidden>
                <Grid {...grid.bidrAprvUsrExcelList.grid} data={fetch.getBidrUsrExcelList.data?.bidrUsrExcelList} />
            </Group>
        </Page>
    );
};
